import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import {
  Files, Plus, Upload, Trash, PencilSimple, Eye, Warning, CheckCircle,
  Clock, Calendar, User, CaretDown, X, Brain, Sparkle, MagnifyingGlass,
  FileText, ArrowClockwise, ChartLine, Flag, Download, PaperPlaneTilt
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Document Types for governance
const DOCUMENT_TYPES = [
  { id: 'policy', label: 'Policy' },
  { id: 'standard', label: 'Standard' },
  { id: 'framework', label: 'Framework' },
  { id: 'guidelines', label: 'Guidelines' },
  { id: 'procedure', label: 'Procedure' },
  { id: 'manual', label: 'Manual' }
];

// Regulatory Themes
const REGULATORY_THEMES = [
  { id: 'cybersecurity', label: 'Cybersecurity' },
  { id: 'data_protection', label: 'Data Protection' },
  { id: 'financial_crime', label: 'Financial Crime' },
  { id: 'tax_compliance', label: 'Tax Compliance' },
  { id: 'corporate_governance', label: 'Corporate Governance' },
  { id: 'consumer_protection', label: 'Consumer Protection' },
  { id: 'employment_labour', label: 'Employment & Labour' },
  { id: 'conduct', label: 'Conduct' },
  { id: 'capital_liquidity', label: 'Capital & Liquidity' },
  { id: 'anti_bribery_corruption', label: 'Anti Bribery and Corruption' },
  { id: 'esg', label: 'ESG' },
  { id: 'general', label: 'General' }
];

// Review status colors
const REVIEW_STATUS_COLORS = {
  current: 'bg-green-500/20 text-green-400 border-green-500/30',
  upcoming: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
  draft: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

const DocumentGovernancePage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNaviReview, setShowNaviReview] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  
  // NAVI Review state
  const [naviMessages, setNaviMessages] = useState([]);
  const [naviInput, setNaviInput] = useState('');
  const [naviLoading, setNaviLoading] = useState(false);
  const [naviAnalysis, setNaviAnalysis] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/document-governance/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/document-governance/stats`);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/api/document-governance/documents/${documentId}`);
      toast.success('Document deleted');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const openNaviReview = async (doc) => {
    setSelectedDocument(doc);
    setShowNaviReview(true);
    setNaviMessages([]);
    setNaviAnalysis(null);
    
    // Automatically trigger NAVI analysis
    setNaviLoading(true);
    try {
      const response = await axios.post(`${API}/api/document-governance/navi-analyze/${doc.id}`);
      setNaviAnalysis(response.data.analysis);
      setNaviMessages([{
        role: 'assistant',
        content: `I've analyzed **${doc.document_name}** (${doc.document_type}).\n\n${response.data.analysis.summary}\n\nWould you like me to elaborate on any specific findings?`
      }]);
    } catch (error) {
      setNaviMessages([{
        role: 'assistant',
        content: `I'm ready to review **${doc.document_name}**. What would you like me to analyze?`
      }]);
    } finally {
      setNaviLoading(false);
    }
  };

  const sendNaviMessage = async () => {
    if (!naviInput.trim() || naviLoading) return;
    
    const userMessage = naviInput.trim();
    setNaviInput('');
    setNaviMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setNaviLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/navi/chat`, {
        message: userMessage,
        mode: 'document_governance',
        context: {
          document_name: selectedDocument?.document_name,
          document_type: selectedDocument?.document_type,
          document_id: selectedDocument?.id,
          version: selectedDocument?.version,
          last_approved: selectedDocument?.last_approved_date
        }
      });
      
      setNaviMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response || response.data.message || 'I apologize, I could not process that request.'
      }]);
    } catch (error) {
      setNaviMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I encountered an error. Please try again.'
      }]);
    } finally {
      setNaviLoading(false);
    }
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [naviMessages]);

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    // Filter by status
    if (filter !== 'all' && doc.review_status !== filter) return false;
    
    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!doc.document_name?.toLowerCase().includes(q) && 
          !doc.policy_owner?.toLowerCase().includes(q) &&
          !doc.document_type?.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  // Calculate days until review
  const getDaysUntilReview = (nextReviewDate) => {
    if (!nextReviewDate) return null;
    const today = new Date();
    const reviewDate = new Date(nextReviewDate);
    const diffTime = reviewDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getReviewStatus = (doc) => {
    const days = getDaysUntilReview(doc.next_review_date);
    if (days === null) return 'draft';
    if (days < 0) return 'overdue';
    if (days <= 30) return 'upcoming';
    return 'current';
  };

  return (
    <div className="p-6 space-y-6" data-testid="document-governance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gold/10 rounded-lg">
            <Files size={28} className="text-gold" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-text-primary">Document Governance</h1>
            <p className="text-sm text-text-tertiary">Manage policies, frameworks, and compliance documents</p>
          </div>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="px-4 py-2 bg-gold text-surface rounded-lg hover:bg-gold-hover transition-colors flex items-center gap-2"
        >
          <Plus size={18} /> Add Document
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Files size={18} className="text-blue-400" />
            <span className="text-sm text-text-tertiary">Total Documents</span>
          </div>
          <p className="text-2xl font-bold text-text-primary">{stats?.total || 0}</p>
        </div>
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-green-400" />
            <span className="text-sm text-text-tertiary">Current</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{stats?.current || 0}</p>
        </div>
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} className="text-amber-400" />
            <span className="text-sm text-text-tertiary">Review Soon</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats?.upcoming || 0}</p>
        </div>
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} className="text-red-400" />
            <span className="text-sm text-text-tertiary">Overdue</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{stats?.overdue || 0}</p>
        </div>
        <div className="bg-surface rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={18} className="text-gold" />
            <span className="text-sm text-text-tertiary">NAVI Reviews</span>
          </div>
          <p className="text-2xl font-bold text-gold">{stats?.navi_reviewed || 0}</p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {['all', 'current', 'upcoming', 'overdue', 'draft'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filter === status
                  ? 'bg-gold text-surface'
                  : 'bg-surface-light text-text-secondary hover:bg-surface-lighter'
              }`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="pl-9 pr-4 py-2 bg-surface-light border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold w-64"
          />
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface-light border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Document</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Theme</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Version</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Owner</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Last Approved</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Next Review</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-text-tertiary uppercase">Status</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-text-tertiary uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-text-tertiary">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading documents...
                </td>
              </tr>
            ) : filteredDocuments.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-text-tertiary">
                  <Files size={48} className="mx-auto mb-2 opacity-50" />
                  No documents found
                </td>
              </tr>
            ) : (
              filteredDocuments.map(doc => {
                const status = getReviewStatus(doc);
                const daysUntil = getDaysUntilReview(doc.next_review_date);
                
                return (
                  <tr key={doc.id} className="hover:bg-surface-light/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-gold" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{doc.document_name}</p>
                          {doc.pdf_filename && (
                            <p className="text-xs text-text-tertiary">{doc.pdf_filename}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-surface-light rounded text-xs text-text-secondary">
                        {doc.document_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-gold/10 text-gold rounded text-xs">
                        {doc.regulatory_theme || 'General'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-secondary font-mono">v{doc.version || '1.0'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-text-tertiary" />
                        <span className="text-sm text-text-secondary">{doc.policy_owner || 'Unassigned'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-secondary">
                        {doc.last_approved_date ? new Date(doc.last_approved_date).toLocaleDateString() : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-sm text-text-secondary">
                          {doc.next_review_date ? new Date(doc.next_review_date).toLocaleDateString() : '-'}
                        </span>
                        {daysUntil !== null && (
                          <p className={`text-xs ${daysUntil < 0 ? 'text-red-400' : daysUntil <= 30 ? 'text-amber-400' : 'text-text-tertiary'}`}>
                            {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : `${daysUntil} days`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs border ${REVIEW_STATUS_COLORS[status]}`}>
                        {status === 'current' && <><CheckCircle size={12} className="inline mr-1" />Current</>}
                        {status === 'upcoming' && <><Clock size={12} className="inline mr-1" />Review Soon</>}
                        {status === 'overdue' && <><Warning size={12} className="inline mr-1" />Overdue</>}
                        {status === 'draft' && <><FileText size={12} className="inline mr-1" />Draft</>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openNaviReview(doc)}
                          className="p-1.5 text-gold hover:bg-gold/10 rounded transition-colors"
                          title="NAVI Review"
                        >
                          <Brain size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowUploadModal(true);
                          }}
                          className="p-1.5 text-text-tertiary hover:text-blue-400 transition-colors"
                          title="Edit"
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="p-1.5 text-text-tertiary hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Upload/Edit Modal */}
      {showUploadModal && (
        <UploadModal
          document={selectedDocument}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedDocument(null);
          }}
          onSave={() => {
            fetchDocuments();
            fetchStats();
            setShowUploadModal(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* NAVI Review Modal */}
      {showNaviReview && selectedDocument && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border bg-gradient-to-r from-gold/5 to-transparent">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/10 rounded-xl">
                  <Brain size={28} className="text-gold" weight="fill" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">NAVI Policy Review</h3>
                  <p className="text-sm text-text-tertiary">{selectedDocument.document_name}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-surface-light rounded text-text-tertiary">
                      {selectedDocument.document_type}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gold/10 rounded text-gold">
                      {selectedDocument.regulatory_theme || 'General'}
                    </span>
                    {selectedDocument.version && (
                      <span className="text-xs text-text-tertiary">v{selectedDocument.version}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNaviReview(false);
                  setSelectedDocument(null);
                  setNaviAnalysis(null);
                  setNaviMessages([]);
                }}
                className="p-2 hover:bg-surface-light rounded-lg transition-colors"
              >
                <X size={24} className="text-text-tertiary" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
              {/* Chat Messages */}
              <div className="p-6 space-y-4">
                {naviMessages.map((msg, idx) => (
                  <div key={idx} className={`${msg.role === 'user' ? 'flex justify-end' : ''}`}>
                    {msg.role === 'assistant' ? (
                      <div className="bg-[#1A1A1A] rounded-xl p-5 border border-border">
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
                          <Brain size={18} className="text-gold" />
                          <span className="text-sm font-medium text-gold">NAVI Analysis</span>
                        </div>
                        <div className="prose prose-invert prose-sm max-w-none">
                          {msg.content.split('\n').map((line, lineIdx) => {
                            // Style different sections
                            const trimmedLine = line.trim();
                            if (!trimmedLine) return <div key={lineIdx} className="h-2" />;
                            
                            // Section headers
                            if (trimmedLine.match(/^(EXECUTIVE SUMMARY|COMPLIANCE SCORE|GAPS IDENTIFIED|REGULATORY UPDATES|RECOMMENDATIONS|PRIORITY ACTIONS)/i)) {
                              return (
                                <h4 key={lineIdx} className="text-base font-semibold text-gold mt-4 mb-2 flex items-center gap-2">
                                  {trimmedLine.includes('GAPS') && <Warning size={16} className="text-red-400" />}
                                  {trimmedLine.includes('RECOMMENDATION') && <Sparkle size={16} className="text-blue-400" />}
                                  {trimmedLine.includes('REGULATORY') && <Flag size={16} className="text-amber-400" />}
                                  {trimmedLine.includes('PRIORITY') && <CheckCircle size={16} className="text-green-400" />}
                                  {trimmedLine.includes('COMPLIANCE SCORE') && <ChartLine size={16} className="text-gold" />}
                                  {trimmedLine}
                                </h4>
                              );
                            }
                            
                            // Section references (e.g., "Section 3.2")
                            if (trimmedLine.match(/^Section \d/i)) {
                              return (
                                <p key={lineIdx} className="text-sm font-medium text-blue-400 mt-3 mb-1">
                                  {trimmedLine}
                                </p>
                              );
                            }
                            
                            // Legislation references
                            if (trimmedLine.match(/(PRECCA|FICA|POPIA|PAIA|Companies Act|King IV|King V)/i)) {
                              return (
                                <p key={lineIdx} className="text-sm text-text-secondary leading-relaxed">
                                  {trimmedLine.split(/(PRECCA|FICA|POPIA|PAIA|Companies Act|King IV|King V)/gi).map((part, partIdx) => {
                                    if (part.match(/(PRECCA|FICA|POPIA|PAIA|Companies Act|King IV|King V)/i)) {
                                      return <span key={partIdx} className="text-amber-400 font-medium">{part}</span>;
                                    }
                                    return part;
                                  })}
                                </p>
                              );
                            }
                            
                            // Numbered items
                            if (trimmedLine.match(/^\d+\./)) {
                              return (
                                <p key={lineIdx} className="text-sm text-text-secondary leading-relaxed pl-4 border-l-2 border-gold/30 my-2">
                                  {trimmedLine}
                                </p>
                              );
                            }
                            
                            // Bullet points
                            if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
                              return (
                                <p key={lineIdx} className="text-sm text-text-secondary leading-relaxed pl-4 my-1">
                                  {trimmedLine}
                                </p>
                              );
                            }
                            
                            // Default paragraph
                            return (
                              <p key={lineIdx} className="text-sm text-text-secondary leading-relaxed">
                                {trimmedLine}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gold/10 rounded-xl p-4 max-w-[80%] border border-gold/20">
                        <p className="text-sm text-text-primary">{msg.content}</p>
                      </div>
                    )}
                  </div>
                ))}
                
                {naviLoading && (
                  <div className="bg-[#1A1A1A] rounded-xl p-5 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="animate-pulse">
                        <Brain size={20} className="text-gold" />
                      </div>
                      <div>
                        <p className="text-sm text-gold font-medium">NAVI is analyzing...</p>
                        <p className="text-xs text-text-tertiary mt-1">Reading document content and checking against regulatory requirements</p>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </div>
            
            {/* Input Area */}
            <div className="p-4 border-t border-border bg-[#1A1A1A]">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={naviInput}
                  onChange={(e) => setNaviInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendNaviMessage()}
                  placeholder="Ask NAVI about specific sections, regulatory requirements, or recommendations..."
                  className="flex-1 px-4 py-3 bg-surface border border-border rounded-xl text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold text-sm"
                />
                <button
                  onClick={sendNaviMessage}
                  disabled={naviLoading || !naviInput.trim()}
                  className="px-5 py-3 bg-gold text-surface rounded-xl hover:bg-gold-hover transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
              <p className="text-xs text-text-tertiary mt-2">
                Try: "What PRECCA amendments should I include?" or "Review Section 5 on third-party due diligence"
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Upload/Edit Modal Component
const UploadModal = ({ document, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    document_name: document?.document_name || '',
    document_type: document?.document_type || 'Policy',
    regulatory_theme: document?.regulatory_theme || 'General',
    version: document?.version || '1.0',
    policy_owner: document?.policy_owner || '',
    last_approved_date: document?.last_approved_date?.split('T')[0] || '',
    next_review_date: document?.next_review_date?.split('T')[0] || '',
    description: document?.description || ''
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          data.append(key, value);
        }
      });
      if (file) {
        data.append('file', file);
      }
      
      if (document?.id) {
        await axios.put(`${API}/api/document-governance/documents/${document.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Document updated');
      } else {
        await axios.post(`${API}/api/document-governance/documents`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Document created');
      }
      onSave();
    } catch (error) {
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl w-full max-w-lg">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-text-primary">
            {document ? 'Edit Document' : 'Add New Document'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-surface-light rounded">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Document Name *</label>
            <input
              type="text"
              value={formData.document_name}
              onChange={(e) => setFormData(prev => ({ ...prev, document_name: e.target.value }))}
              className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold"
              placeholder="Enter document name"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Document Type *</label>
              <select
                value={formData.document_type}
                onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary focus:outline-none focus:border-gold appearance-none cursor-pointer"
                style={{ colorScheme: 'dark' }}
              >
                {DOCUMENT_TYPES.map(type => (
                  <option key={type.id} value={type.label} className="bg-[#1A1A1A] text-text-primary">{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Regulatory Theme *</label>
              <select
                value={formData.regulatory_theme}
                onChange={(e) => setFormData(prev => ({ ...prev, regulatory_theme: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary focus:outline-none focus:border-gold appearance-none cursor-pointer"
                style={{ colorScheme: 'dark' }}
              >
                {REGULATORY_THEMES.map(theme => (
                  <option key={theme.id} value={theme.label} className="bg-[#1A1A1A] text-text-primary">{theme.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold"
                placeholder="1.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Policy Owner</label>
              <input
                type="text"
                value={formData.policy_owner}
                onChange={(e) => setFormData(prev => ({ ...prev, policy_owner: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold"
                placeholder="Name or department"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Last Approved Date</label>
              <input
                type="date"
                value={formData.last_approved_date}
                onChange={(e) => setFormData(prev => ({ ...prev, last_approved_date: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary focus:outline-none focus:border-gold"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Next Review Date</label>
              <input
                type="date"
                value={formData.next_review_date}
                onChange={(e) => setFormData(prev => ({ ...prev, next_review_date: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary focus:outline-none focus:border-gold"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 bg-[#1A1A1A] border border-border rounded-lg text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-gold h-20 resize-none"
              placeholder="Brief description of the document..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Upload Document (PDF or Word)</label>
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center bg-[#1A1A1A] hover:border-gold/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload size={24} className="mx-auto text-text-tertiary mb-2" />
                <p className="text-sm text-text-secondary">
                  {file ? file.name : 'Click to upload or drag & drop'}
                </p>
                <p className="text-xs text-text-tertiary mt-1">PDF, DOC, DOCX up to 50MB</p>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:bg-[#242424] rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.document_name}
              className="px-4 py-2 bg-gold text-surface rounded-lg hover:bg-gold-hover transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : document ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentGovernancePage;
